/**
 * Shared allowlist for distributed tracing.
 * Provides the base domains that should be traced, platform-agnostic.
 */

import { logError } from '@bts-web/utils-functions';

/**
 * Returns the base API domain for tracing whitelisting.
 */
export const getApiDomain = (): string | null => {
  const apiUrl = process.env.NEXT_PUBLIC_GQL_API_ENDPOINT_URL;

  if (!apiUrl) return null;

  try {
    const url = new URL(apiUrl);

    return url.origin;
  } catch {
    return null;
  }
};

/**
 * Returns all domains that should be whitelisted for tracing.
 * Includes API domain and VERCEL_URL for preview environments.
 */
export const getTracingDomains = (): string[] => {
  const domains: string[] = [];

  // Add API domain
  const apiDomain = getApiDomain();

  if (apiDomain) {
    domains.push(apiDomain);
  }

  let vercelUrl = process.env.VERCEL_URL ?? window.location.origin;

  if (vercelUrl) {
    try {
      vercelUrl = vercelUrl.startsWith('https')
        ? vercelUrl
        : `https://${vercelUrl}`;

      const url = new URL(vercelUrl);

      domains.push(url.origin);
    } catch (error) {
      logError(`Could not enable tracing for deployment URL "${vercelUrl}"}`, {
        cause: error,
      });
    }
  }

  return domains;
};

/**
 * Checks if a URL should receive trace headers.
 */
export const isAllowedTracingUrl = (url: string): boolean => {
  // First check: deny static assets and Next.js files early
  if (typeof window !== 'undefined') {
    if (
      url.includes('/_next/') ||
      url.includes('.ico') ||
      url.includes('.png') ||
      url.includes('.jpg') ||
      url.includes('.woff')
    ) {
      return false;
    }
  }

  // Then check configured domains (for external APIs like GraphQL and origin)
  const domains = getTracingDomains();

  if (domains.some((domain) => url.startsWith(domain))) {
    return true;
  }

  // Allow all same-origin requests (for Server Actions and internal routes)
  if (typeof window !== 'undefined') {
    try {
      const urlObj = new URL(url, window.location.origin);

      if (urlObj.origin === window.location.origin) {
        return true;
      }
    } catch {
      return false;
    }
  }

  return false;
};
